import { useEffect, useRef, useState } from 'react';
import { Link } from '../../router/Link';
import {
  Button,
  ConfirmButton,
  Eyebrow,
  LoadingStatus,
  Notice,
  Spinner,
  Thumbnail,
  buttonClass,
  cx,
} from '../../ui';
import { ThumbnailOverlay } from '../../listing/ThumbnailOverlay';
import { useDeArrowBranding } from '../../dearrow/useDeArrowBranding';
import { SaveQueueToPlaylist } from '../../queue/SaveQueueToPlaylist';
import { watchPlaylistPage, WATCH_PLAYLIST_PAGE_SIZE, type WatchPlaylistRow } from './model';
import { SectionHead } from './SectionHead';
import type { WatchListKeep } from './useWatchScreen';
import type { WatchListMove, WatchPlaylistBinding } from './useWatchPlaylist';
import { useCopy } from '../../strings/useCopy';
import styles from './playlistPanel.module.css';

/** What a row needs to offer removal: the act, which rows have one in flight, and whether it asks. */
interface EntryRemove {
  readonly act: (removeKey: string) => void;
  readonly busy: ReadonlySet<string>;
  /**
   * Whether the button asks a second time before it acts, which is a different answer per list.
   *
   * **A playlist row asks**: the press is a write to the reader's account that no press here takes
   * back, and it is the same act the playlist page's own rows offer behind the same two clicks.
   * **A queue row does not**: the list is this tab's, the row goes out of an array, and the panel it
   * sits in already empties the whole queue in one press. A row that asked twice while Clear beside it
   * asks once had the grammar backwards.
   */
  readonly confirm: boolean;
}

/**
 * The words this panel needs **per list** — a row's and a control's, which have to say which list
 * the reader is acting on. Both blocks in `strings/en/watch.ts` satisfy it, and the two lists really
 * do need their own sentences for the same controls: a queue's Autoplay is its own stored switch, its
 * Reverse moves the rows for good, and the act at the far end empties one list while it merely lets go
 * of the other.
 *
 * The **head** is not in here, and that is the naming rule rather than an omission: on the watch page
 * the side column's list is called a playlist whichever list it is, so the heading and the two
 * Show/Hide controls come from the playlist's block for both. Which list is open is said by the
 * heading a line below, which reads "Queue".
 */
export interface PanelCopy {
  readonly autoplay: string;
  readonly autoplayDetail: string;
  readonly reverse: string;
  readonly reverseDetail: string;
  /** The accessible name of the act at the far end, which is a different act for each list. */
  readonly clear: string;
  /** What that one press costs, since neither list asks a second time — see the control itself. */
  readonly clearDetail: string;
  readonly previousEntry: string;
  readonly nextEntry: string;
  readonly previousLabel: string;
  readonly nextLabel: string;
  readonly nowPlaying: string;
  readonly entryGone: string;
  readonly remove: (title: string) => string;
  readonly removeTitle: string;
  readonly removing: string;
  readonly loading: string;
  readonly problem: string;
}

/**
 * The list panel: the list the reader is watching *through*, at the top of the side column, above
 * chapters. It behaves like the two panels below it — hideable, its way back a pill in the actions row
 * when the column is beside the video, its own Show/Hide header when the column has stacked under the
 * description — and it draws the queue, which is a window on a playlist whenever the address names one.
 *
 * Two things are its own. The head carries the list's **name** and its position in it ("451 of 575"),
 * because a panel that shows four rows of a list needs to say which list and where in it; on a playlist
 * the name is a link out to the playlist page. And under it a **row of controls over the list**: back and
 * forward through the rows, autoplay, letting go of the list, keeping it and the order switch. The panel
 * is the only place in the app where "what plays next" is a question, and answering it anywhere else
 * would be answering it about nothing.
 *
 * The transport acts on the order as **shown**, which is what makes reversing the list mean something:
 * flipped, forward goes towards entry 1 and autoplay follows it there. Autoplay is the reader's
 * arrangement of this page (`watchLayout`); a playlist's order is a property of the playlist, remembered
 * per playlist in this browser and shared with the playlist page's own Order menu.
 *
 * The window is four rows with the rail's pager under it, and it *opens* on the window holding the
 * playing row — which is what "visible on load" means when the list is 575 long and the column is four
 * rows tall. `binding.initialPage` moves under the panel only while the run is still reaching that row,
 * so a reader who has paged somewhere else keeps their place (`pinned`). The pager is also where the run
 * grows: a playlist holds hundreds of rows the panel has not read, in **both** directions, since a list
 * opened at entry 350 has 350 entries in front of it.
 */
export function PlaylistPanel({
  binding,
  layout,
  onClear,
  keep,
  stacked,
  shown,
  kind = 'playlist',
}: {
  readonly binding: WatchPlaylistBinding;
  readonly layout: {
    readonly onHide: () => void;
    readonly autoplay: boolean;
    readonly onAutoplayChange: (on: boolean) => void;
  };
  /**
   * Stop watching this list — already resolved to the act this list's version of it is
   * (`useWatchScreen`'s `clearList`). One press either way: the panel draws the same control for both
   * and only the words differ, so nothing here has to know which list it is acting on.
   */
  readonly onClear: () => void;
  /**
   * The control between Clear and the order switch, which is a different act on each list and therefore
   * two faces of one button rather than two buttons — see {@link WatchListKeep}.
   */
  readonly keep: WatchListKeep;
  /** Below the description rather than beside the video — the comments header's shape. */
  readonly stacked: boolean;
  readonly shown: boolean;
  /**
   * Which list this is. The panel is the same one either way — the same head, window, pager, rows and
   * controls — and this prop is what picks the **words**, which are per-list because the same control
   * means a different thing on each: the act at the far end empties a queue for good, while on a
   * playlist it changes nothing but the address.
   *
   * One control reads it for more than words: the fifth one is either letting go of a playlist or saving
   * a list of the reader's own as one, and those are two acts rather than two sentences about one
   * ({@link WatchListKeep}).
   *
   * The order switch does not read this. It reads `binding.order`, which carries whether the flip is a
   * remembered display state or a real reorder — a fact about the list rather than about the panel, and
   * one the two hooks already know.
   */
  readonly kind?: 'playlist' | 'queue';
}) {
  const copy = useCopy().text.watch;
  const words: PanelCopy = kind === 'queue' ? copy.queue : copy.playlist;
  /** Narrowed here rather than in the JSX, so the handlers close over the arm and not over the union. */
  const order = binding.order;
  /** The head names the column's list, which this page calls a playlist either way — see {@link PanelCopy}. */
  const head = copy.playlist;
  const [page, setPage] = useState(binding.initialPage);
  /** Set the moment the reader works the pager: from then on the window is theirs, not the run's. */
  const pinned = useRef(false);

  // The playing row can arrive several pages into the run, so the opening window is not knowable
  // at mount. Following it until the reader takes over is what makes "visible on load" true for a
  // long playlist without the panel ever yanking a window out from under a deliberate press.
  useEffect(() => {
    if (!pinned.current) setPage(binding.initialPage);
  }, [binding.initialPage]);

  /**
   * The row the reader asked to see the rows *before*, while the request that brings them is on the wire.
   *
   * A page number counts from the top row, and a run that grows at its **start** moves every row already
   * drawn down by however many arrive — so the window the number names would be somewhere nobody asked
   * for. Holding the row instead is what lets the window land exactly one step back once they land.
   * Adjusted during render rather than in an effect, `useWatchScreen`'s own pattern for this, so no frame
   * shows the wrong window.
   */
  const [before, setBefore] = useState<string | null>(null);
  const beforeAt = before === null ? -1 : binding.rows.findIndex((row) => row.key === before);
  if (before !== null && beforeAt > 0) {
    setBefore(null);
    setPage(Math.floor((beforeAt - 1) / WATCH_PLAYLIST_PAGE_SIZE));
  }

  const view = watchPlaylistPage(binding.rows, page);
  const prevEnabled = (view.pager?.prevEnabled ?? false) || binding.hasEarlier;
  const nextEnabled = (view.pager?.nextEnabled ?? false) || binding.hasMore;
  const turn = (to: number): void => {
    pinned.current = true;
    // A step off either end of what is loaded is where "load more" lives on this panel, since a six-row
    // window with its own button under the pager would be two controls for one act. Backwards the rows
    // arrive *above* the window, so the step is taken once they are here rather than now.
    if (to < view.page && !(view.pager?.prevEnabled ?? false)) {
      setBefore(view.rows[0]?.key ?? null);
      binding.loadEarlier();
      return;
    }
    setPage(to);
    if (to > view.page && !(view.pager?.nextEnabled ?? false)) binding.loadMore();
  };

  return (
    <section className={styles.panel} aria-label={head.label}>
      {stacked ? (
        <SectionHead
          className={styles.stackedHead}
          headingClassName={styles.stackedHeading}
          heading={head.heading}
          shown={shown}
          onToggle={layout.onHide}
        >
          <Button
            variant="subtle"
            className={styles.stackedToggle}
            label={shown ? head.hideList : head.showList}
            ariaExpanded={shown}
            onClick={layout.onHide}
          >
            {shown ? head.hide : head.show}
          </Button>
        </SectionHead>
      ) : (
        <div className={styles.head}>
          <Eyebrow>{head.heading}</Eyebrow>
          <Button variant="subtle" label={head.hideList} onClick={layout.onHide}>
            {head.hide}
          </Button>
        </div>
      )}

      {!shown ? null : (
        <>
          {/* Which list, and where in it. On a playlist the title is a link out to the playlist page,
              the one place the whole list, its description and its editing live. A queue lives in this
              panel and has nowhere to lead, so it says its name in plain text. */}
          <div className={styles.about}>
            {binding.href === null ? (
              <span className={styles.title} title={binding.title ?? undefined}>
                {binding.title ?? head.heading}
              </span>
            ) : (
              <Link className={styles.title} href={binding.href} title={binding.title ?? undefined}>
                {binding.title ?? head.heading}
              </Link>
            )}
            {binding.positionText === null ? null : (
              <span className={styles.position}>{binding.positionText}</span>
            )}
          </div>

          {/*
           * The acts on the list, over the list. Back, Autoplay and forward read as one transport
           * group because they are one question — what plays next — and the three acts on the list as a
           * whole sit together at the far end, where every other list in the app keeps its ordering
           * control (CLAUDE.md). They run from the one that ends the list to the one that keeps it to
           * the one that rearranges it, so the order switch stays the last thing on the row it has
           * always been.
           */}
          <div className={styles.controls}>
            <div className={styles.controlGroup}>
              <EntryStep
                href={binding.previous?.href ?? null}
                label={words.previousEntry}
                direction="back"
              />
              <Button
                variant="ghost"
                size="sm"
                icon
                className={cx(styles.control, layout.autoplay && styles.controlArmed)}
                label={words.autoplay}
                title={words.autoplayDetail}
                ariaPressed={layout.autoplay}
                onClick={() => layout.onAutoplayChange(!layout.autoplay)}
              >
                <AutoplayGlyph />
              </Button>
              <EntryStep
                href={binding.next?.href ?? null}
                label={words.nextEntry}
                direction="forward"
              />
            </div>
            <div className={styles.controlGroup}>
              {/*
               * One press for either list, and no confirm on either. The panel is where a reader is
               * *watching* a list, so both of these are course corrections taken mid-video, and a
               * second press between the reader and the one they meant is the wrong trade in a row of
               * five one-press controls. The stakes are in the `title` instead, which is where a
               * control that cannot be pressed back says so.
               */}
              <Button
                variant="ghost"
                size="sm"
                icon
                className={styles.control}
                label={words.clear}
                title={words.clearDetail}
                onClick={onClear}
              >
                <ClearGlyph />
              </Button>
              {/*
               * Keeping the list, in its two faces. On a playlist it lets go of the link and keeps the
               * rows the reader has loaded; on a list of their own it offers to save the whole thing into
               * a playlist, which is the only way a queue outlives the tab. The words for the first one
               * come from the playlist's own block whichever `words` is in hand, since that face exists
               * on one list only.
               */}
              {kind === 'playlist' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  icon
                  className={styles.control}
                  label={copy.playlist.unlink}
                  title={copy.playlist.unlinkDetail}
                  onClick={keep.unlink}
                >
                  <UnlinkGlyph />
                </Button>
              ) : (
                <SaveQueueToPlaylist
                  className={styles.control}
                  onSaved={keep.linkTo}
                  signInHref={keep.signInHref}
                />
              )}
              {/*
               * The order switch, in its two readings. A playlist's flip is a *state* the browser
               * remembers, so it is a switch that reads as on and says so with `aria-pressed`. A
               * queue's is an *act*: the rows really move, so afterwards there is nothing to be on
               * about — the list is simply in this order, and a second press turns it back because
               * reversing twice is the identity.
               */}
              {order.kind === 'display' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  icon
                  className={cx(styles.control, order.reversed && styles.controlOn)}
                  label={words.reverse}
                  title={words.reverseDetail}
                  ariaPressed={order.reversed}
                  onClick={() => order.setReversed(!order.reversed)}
                >
                  <ReverseGlyph />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  icon
                  className={styles.control}
                  label={words.reverse}
                  title={words.reverseDetail}
                  onClick={order.reverse}
                >
                  <ReverseGlyph />
                </Button>
              )}
            </div>
          </div>

          {binding.problem === null ? null : <Notice tone="caution">{binding.problem}</Notice>}
          {/* A refused removal, said where the press was — `live`, because nothing else on screen
              changed when it failed (the playlist page's entry notice, same shape). */}
          {binding.entryProblem === null ? null : (
            <Notice tone="caution" live>
              <strong>{binding.entryProblem.title}.</strong> {binding.entryProblem.detail}
            </Notice>
          )}

          {binding.loading ? <LoadingStatus>{words.loading}</LoadingStatus> : null}

          <ol className={styles.list}>
            {view.rows.map((row) => (
              <EntryRow
                key={row.key}
                row={row}
                words={words}
                move={binding.move}
                remove={
                  binding.canRemove
                    ? {
                        act: binding.removeEntry,
                        busy: binding.removingKeys,
                        confirm: kind === 'playlist',
                      }
                    : null
                }
              />
            ))}
          </ol>

          {view.pager === null && !binding.hasMore && !binding.hasEarlier ? null : (
            <div className={styles.pager}>
              {/* The rail's own pager: round < > with the position between them. */}
              <Button
                variant="pill"
                size="sm"
                icon
                disabled={!prevEnabled || binding.loadingMore}
                label={words.previousLabel}
                onClick={() => turn(view.page - 1)}
              >
                <ChevronGlyph direction="back" />
              </Button>
              <span className={styles.pageText} aria-hidden="true">
                {view.pager?.pageText ?? ''}
              </span>
              <Button
                variant="pill"
                size="sm"
                icon
                disabled={!nextEnabled || binding.loadingMore}
                label={words.nextLabel}
                onClick={() => turn(view.page + 1)}
              >
                <ChevronGlyph direction="forward" />
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * One step through the playlist, back or forward.
 *
 * A **link** while there is somewhere to go, so middle-click, ⌘-click and "copy link address" work
 * on it the way they do on the rows themselves (`ui/buttonClass.ts` draws this line for the whole
 * app), and a disabled button at the ends of the list, since there is no such thing as a disabled
 * anchor. Which end that is depends on the display order: reversed, forward goes towards entry 1.
 */
function EntryStep({
  href,
  label,
  direction,
}: {
  readonly href: string | null;
  readonly label: string;
  readonly direction: 'back' | 'forward';
}) {
  const glyph = <StepGlyph direction={direction} />;
  if (href === null) {
    return (
      <Button
        variant="ghost"
        size="sm"
        icon
        className={styles.control}
        label={label}
        title={label}
        disabled
      >
        {glyph}
      </Button>
    );
  }
  return (
    <Link
      className={buttonClass({ variant: 'ghost', size: 'sm', icon: true, className: styles.control })}
      href={href}
      title={label}
      ariaLabel={label}
    >
      {glyph}
    </Link>
  );
}

/**
 * One entry. The rail row's shape, for the rail row's reasons: the **whole row** is the
 * entry's link — the title's anchor stretched over it by a pseudo-element, with the channel's lifted
 * back above — because an anchor inside an anchor is invalid HTML and the channel name is a second,
 * different destination. The remove button in the corner stays above the stretch too. With the
 * **position riding the thumbnail's corner** the way the playlist
 * page's cards wear theirs, and the playing one marked by `aria-current` and a wash rather than by
 * colour alone: the words "Now playing" are there for a screen reader, inside the link's own text.
 * No glyph in place of the playing row's number: the number is what a reader is looking for, and the
 * wash already says which row is playing.
 *
 * A dead entry is not a link at all: its id is real, so an anchor would look live and land on
 * "Video not found". The numbering keeps it, which is the same call the playlist page makes.
 *
 * **No hover preview**, deliberately: the recommendations rail sits directly under this list in the
 * same column and has none, and two lists an inch apart where only one of them animates reads as a
 * bug in the other. The flipbook belongs to the grids (`listing/useGridPreview.ts`); if the side
 * column ever gets it, both lists get it together.
 *
 * The **watched** treatment is that same rule pointing the other way — the rail draws all four marks,
 * so this list does too: the faded row, the resume bar along the picture's bottom edge, the "Watched"
 * mark in its corner and the word inside the link for a screen reader. The playing row is exempt from
 * all of it — see {@link WatchPlaylistRow.watched}.
 */
function EntryRow({
  row,
  words,
  move,
  remove,
}: {
  readonly row: WatchPlaylistRow;
  /** Whose list this row is in — a queue row must not offer to remove itself "from this playlist". */
  readonly words: PanelCopy;
  /** The up and down arrows, which only a queue of the reader's own has (`WatchListMove`). */
  readonly move: WatchListMove | null;
  readonly remove: EntryRemove | null;
}) {
  const copy = useCopy().text;
  const dearrow = useDeArrowBranding(row.unavailable ? null : row.videoId);
  const title = dearrow.title ?? row.title;

  const frame = (
    <>
      <Thumbnail
        src={dearrow.thumbnailUrl ?? row.thumbnailUrl}
        badge={row.badge}
        progress={row.progress}
        /* No `preview`: this list runs no flipbook, so the corner note is only ever the mark. It
           sits at the frame's bottom-left, where neither the position chip above it nor the
           duration badge opposite it has anything to collide with. */
        overlay={<ThumbnailOverlay watched={row.watched} />}
      />
      <span className={styles.indexChip} aria-hidden="true">
        {row.position}
      </span>
    </>
  );

  /*
   * The playlist page's per-card removal, over the same corner of the frame — a *sibling* of
   * the thumbnail link, because a `<button>` inside an `<a>` is invalid HTML. A dead entry keeps its
   * button: a video YouTube no longer serves is exactly what a reader wants out of the list. The
   * playing row keeps it too, and taking it out moves the list on to the row after it
   * (`core/queue/model.ts`) rather than leaving a hole where the cursor was.
   */
  const removing = row.removeKey !== null && remove !== null && remove.busy.has(row.removeKey);
  /* The face is the same either way, and only the number of presses behind it differs — see
     {@link EntryRemove.confirm}. The short act is the hover word; the accessible name names the
     video, which a screen reader has no card to look at for. */
  const removeFace = {
    size: 'sm',
    variant: 'ghost',
    icon: true,
    label: removing ? words.removing : words.remove(title),
    title: removing ? words.removing : words.removeTitle,
    disabled: removing,
  } as const;
  /*
   * Left of the remove, in the same box, so the three share its hover reveal. The ends keep both
   * arrows and disable the one with nowhere to go, so the pair never shifts under the pointer.
   */
  const moveWords = copy.watch.queue;
  const moveButtons =
    move === null ? null : (
      <>
        <Button
          size="sm"
          variant="ghost"
          icon
          label={moveWords.moveUp(title)}
          title={moveWords.moveUpTitle}
          disabled={row.key === move.firstKey}
          onClick={() => move.act(row.key, -1)}
        >
          <MoveGlyph direction="up" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          icon
          label={moveWords.moveDown(title)}
          title={moveWords.moveDownTitle}
          disabled={row.key === move.lastKey}
          onClick={() => move.act(row.key, 1)}
        >
          <MoveGlyph direction="down" />
        </Button>
      </>
    );
  const removeButton =
    remove !== null && row.removeKey !== null ? (
      <>
        {remove.confirm ? (
          /* First click arms it — red, wearing the confirm word — second click removes. */
          <ConfirmButton
            {...removeFace}
            confirmLabel={copy.shell.confirm}
            confirmTitle={words.remove(title)}
            onConfirm={() => remove.act(row.removeKey ?? '')}
          >
            {removing ? <Spinner /> : <RemoveGlyph />}
          </ConfirmButton>
        ) : (
          <Button {...removeFace} onClick={() => remove.act(row.removeKey ?? '')}>
            {removing ? <Spinner /> : <RemoveGlyph />}
          </Button>
        )}
      </>
    ) : null;
  const actions =
    moveButtons === null && removeButton === null ? null : (
      <span className={styles.entryActions}>
        {moveButtons}
        {removeButton}
      </span>
    );

  return (
    <li
      className={cx(styles.row, row.current && styles.rowCurrent, row.watched && styles.watched)}
      aria-current={row.current ? 'true' : undefined}
    >
      <span className={styles.frame}>
        {/* Not a link of its own: the title's is stretched across the row, so the
            picture is part of that one target instead of a second anchor to the same place. */}
        <span className={styles.thumb}>{frame}</span>
        {actions}
      </span>

      <div className={styles.text}>
        {row.unavailable ? (
          <span className={styles.entryGone}>{words.entryGone}</span>
        ) : (
          <Link className={styles.entryTitle} href={row.href} title={title}>
            {row.current ? <span className={styles.srOnly}>{words.nowPlaying} — </span> : null}
            {/* The chip is `aria-hidden` and the number is spoken here instead, so a reader hears
                "14. Under Pressure" as one label — and because the `<ol>`'s own ordinal counts the
                four-row window, not the playlist (the playlist page's rule, for the same reason). */}
            <span className={styles.srOnly}>{row.position}. </span>
            {/* The bar and the mark ride inside a frame nothing spoken reaches, so the word goes
                here — inside the link's own text, the grids' rule. */}
            {row.marker === null ? null : <span className={styles.srOnly}>{row.marker.label} — </span>}
            {title}
          </Link>
        )}
        {row.author === null ? null : row.authorHref === null ? (
          <span className={styles.author} title={row.author}>
            {row.author}
          </span>
        ) : (
          /* The channel is a link here too — a name that is a destination everywhere else in
             the app must not be inert in this one list. */
          <Link className={styles.author} href={row.authorHref} title={row.author}>
            {row.author}
          </Link>
        )}
      </div>
    </li>
  );
}

/** A queue row's up and down arrows: the pager's chevron turned on its side. */
function MoveGlyph({ direction }: { readonly direction: 'up' | 'down' }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        d={direction === 'up' ? 'M6 14.5 12 9l6 5.5' : 'M6 9.5 12 15l6-5.5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The playlist page's remove glyph, redrawn locally — `ui/icons.tsx` keeps only the shared set. */
function RemoveGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 12h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Autoplay: a screen that plays on its own — a rounded square with a play triangle in it.
 *
 * **Drawn around what it must not be mistaken for.** The obvious picture is a circular arrow with a
 * play inside, and that is the *replay* glyph in every media player there is — which would promise
 * the one thing this switch does not do, since auto-advance never wraps (`core/queue/model.ts`'s
 * `queueNeighbours`).
 * A list with a play mark is out for the same kind of reason: the actions row's `PlaylistPanelIcon`
 * is already a list, and two list glyphs on one screen read as one control drawn twice.
 *
 * A near-square, not `TheaterIcon`'s 3:2 frame, and a triangle where that one has a second
 * rectangle — the two are on different surfaces (its home is the player bar) and share no outline.
 */
function AutoplayGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <rect
        x="3.5"
        y="4.5"
        width="17"
        height="15"
        rx="2.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path fill="currentColor" d="M10 8.4 15.6 12 10 15.6z" />
    </svg>
  );
}

/**
 * Stop watching this list: a list with a stroke through it.
 *
 * **Struck rather than tapered**, and that is the whole of the drawing. Lines of decreasing length are
 * what every icon set means by *sort*, and this button stands directly beside the order switch — the
 * two read as a matched pair of ordering controls, which is the one reading this glyph cannot afford.
 * A stroke means "not this" in every set there is, and neither arm of this button leaves the list in
 * the column afterwards.
 *
 * **Drawn around the four pictures it must not be mistaken for.** A bin says "delete something on a
 * server", which is what neither arm of this button does. A plain ✕ is the corner player's own and
 * closes a box rather than acting on a list. The row remove's circle-with-a-bar is a few centimetres
 * below this in the same panel and names *one entry*. And `PlaylistPanelIcon` in the actions row is a
 * plain list saying "show this list", so the same list with a stroke over it is its opposite in the
 * same vocabulary.
 *
 * The stroke runs at `MiniNoticeLine`'s angle, which is the app's one struck glyph — that one crosses a
 * circle and says a *video* is not on offer, and the two are never on screen together, since it lives
 * on the corner player's tile.
 */
function ClearGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M4.5 6.5h15M4.5 12h15M4.5 17.5h15" />
        {/* Over the lines rather than between them, and long enough to leave the glyph at both ends:
            a stroke that stopped inside the list would read as a fourth, diagonal row. */}
        <path d="M3.5 19.5 20.5 4.5" />
      </g>
    </svg>
  );
}

/**
 * Let go of the playlist and keep its rows: a chain with a break in it.
 *
 * **A chain, because the thing being ended is a link.** The queue is a window on that playlist and this
 * is the act that closes it, and a snapped chain is what every icon set means by exactly that. It is the
 * only chain in the app, so there is nothing for it to be confused with — and the three glyphs it stands
 * among are each a different picture: Clear beside it is a struck *list*, the row remove a few
 * centimetres below is a circle with a bar, and the order switch on its other side is a pair of arrows.
 *
 * The two halves lie along the row rather than diagonally, which keeps them clear of Clear's diagonal
 * stroke, and the **break is drawn** rather than implied: two open links with a gap between them are
 * what most sets use for *link*, so the pair of short strokes springing away from the gap is what makes
 * this the opposite of that.
 */
function UnlinkGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M10 8.5H7.5a3.5 3.5 0 0 0 0 7H10" />
        <path d="M14 8.5h2.5a3.5 3.5 0 0 1 0 7H14" />
        {/* The snap: the ends spring apart, above the gap and below it. */}
        <path d="M11.4 7 10.7 4.8M12.6 17 13.3 19.2" />
      </g>
    </svg>
  );
}

/**
 * Reverse the order: two arrows trading places, turned upright.
 *
 * The standard swap glyph read vertically, because a list runs down the screen and the act is about
 * which end it starts at. Horizontal arrows would say "go somewhere else", which is what an arrow
 * means on the actions row.
 */
function ReverseGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8.5 4.5v15M8.5 19.5 5 16M8.5 19.5 12 16" />
        <path d="M15.5 19.5v-15M15.5 4.5 12 8M15.5 4.5 19 8" />
      </g>
    </svg>
  );
}

/**
 * A step to the entry either side of this one — the transport glyph, a triangle against a bar,
 * rather than the pager's bare chevron below. The two controls do different things a few
 * centimetres apart: this one changes the video, that one turns the page of a list that stays put.
 */
function StepGlyph({ direction }: { readonly direction: 'back' | 'forward' }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <g transform={direction === 'forward' ? undefined : 'translate(24 0) scale(-1 1)'}>
        <path fill="currentColor" d="M6 5.5 15.5 12 6 18.5z" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          d="M17.8 5.5v13"
        />
      </g>
    </svg>
  );
}

/** The rail pager's arrow — drawn here too, the "a glyph one component draws lives with it" rule. */
function ChevronGlyph({ direction }: { readonly direction: 'back' | 'forward' }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path
        d={direction === 'back' ? 'M14.5 6 9 12l5.5 6' : 'M9.5 6 15 12l-5.5 6'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
